import type { JSX } from 'react';
import { useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import PrivateRoute from './PrivateRoute';
import { trackPageView } from '../services/analytics';
import { ViewProvider } from '../components/layout/ViewContext'; // Import ViewProvider
import AuditLogList from '../pages/admin/auditLogs/List';
import BulkEmailCreate from '../pages/admin/bulkEmail/Create';
import BulkEmailEdit from '../pages/admin/bulkEmail/Edit';
import BulkEmailList from '../pages/admin/bulkEmail/List';
import CompanyInfoCreate from '../pages/admin/companyInfo/Create';
import CompanyInfoEdit from '../pages/admin/companyInfo/Edit';
import CompanyInfoList from '../pages/admin/companyInfo/List';
import Dashboard from '../pages/admin/Dashboard';
import UserCreate from '../pages/admin/users/Create';
import UserEdit from '../pages/admin/users/Edit';
import UsersList from '../pages/admin/users/List';
import ForgotPassword from '../pages/public/ForgotPassword';
import Login from '../pages/public/Login';
import ResetPassword from '../pages/public/ResetPassword';
import Unsubscribe from '../pages/public/Unsubscribe';
import CancelApplication from '../pages/public/CancelApplication';
import Join from '../pages/public/Join';
import Contact from '../pages/public/Contact';
import ProofOfPaymentPage from '../pages/public/ProofOfPaymentPage';
import PublicRangeCreate from '../pages/public/RangeCreate';
import PublicListingCreate from '../pages/public/ListingCreate';
import ListingSearch from '../pages/public/ListingSearch';
import RatingThankYou from '../pages/public/RatingThankYou';
import DebitOrderMandateForm from '../pages/public/DebitOrderMandateForm';
import { infoHubRoutes } from '../modules/infoHub';
import { getFirstAvailableRoute } from '../utils/routePermissions';
import { getPermissions } from '../utils/tokenUtils';

import FirearmTypeList from '../pages/admin/firearmType/List';
import FirearmTypeCreate from '../pages/admin/firearmType/Create';
import FirearmTypeEdit from '../pages/admin/firearmType/Edit';

import FirearmSpecificList from '../pages/admin/firearmSpecific/List';
import FirearmSpecificCreate from '../pages/admin/firearmSpecific/Create';
import FirearmSpecificEdit from '../pages/admin/firearmSpecific/Edit';

import FirearmMakeList from '../pages/admin/firearmMake/List';
import FirearmMakeCreate from '../pages/admin/firearmMake/Create';
import FirearmMakeEdit from '../pages/admin/firearmMake/Edit';

import GradingTypeList from '../pages/admin/gradingType/List';
import GradingTypeCreate from '../pages/admin/gradingType/Create';
import GradingTypeEdit from '../pages/admin/gradingType/Edit';

import GradingCategoryList from '../pages/admin/gradingCategory/List';
import GradingCategoryCreate from '../pages/admin/gradingCategory/Create';
import GradingCategoryEdit from '../pages/admin/gradingCategory/Edit';

import ExerciseTypeList from '../pages/admin/exerciseType/List';
import ExerciseTypeCreate from '../pages/admin/exerciseType/Create';
import ExerciseTypeEdit from '../pages/admin/exerciseType/Edit';

import ExerciseActivityTargetList from '../pages/admin/exerciseActivityTarget/List';
import ExerciseActivityTargetCreate from '../pages/admin/exerciseActivityTarget/Create';
import ExerciseActivityTargetEdit from '../pages/admin/exerciseActivityTarget/Edit';

import ExerciseTargetList from '../pages/admin/exerciseTarget/List';
import ExerciseTargetCreate from '../pages/admin/exerciseTarget/Create';
import ExerciseTargetEdit from '../pages/admin/exerciseTarget/Edit';

import ExerciseSpecificList from '../pages/admin/exerciseSpecific/List';
import ExerciseSpecificCreate from '../pages/admin/exerciseSpecific/Create';
import ExerciseSpecificEdit from '../pages/admin/exerciseSpecific/Edit';

import FirearmBarrelMakeList from '../pages/admin/firearmBarrelMake/List';
import FirearmBarrelMakeCreate from '../pages/admin/firearmBarrelMake/Create';
import FirearmBarrelMakeEdit from '../pages/admin/firearmBarrelMake/Edit';

import FirearmCalibreList from '../pages/admin/firearmCalibre/List';
import FirearmCalibreCreate from '../pages/admin/firearmCalibre/Create';
import FirearmCalibreEdit from '../pages/admin/firearmCalibre/Edit';

import FirearmBarrelList from '../pages/admin/firearmBarrel/List';
import FirearmBarrelCreate from '../pages/admin/firearmBarrel/Create';
import FirearmBarrelEdit from '../pages/admin/firearmBarrel/Edit';

import FirearmActionList from '../pages/admin/firearmAction/List';
import FirearmActionCreate from '../pages/admin/firearmAction/Create';
import FirearmActionEdit from '../pages/admin/firearmAction/Edit';

import FirearmPurposeList from '../pages/admin/firearmPurpose/List';
import FirearmPurposeCreate from '../pages/admin/firearmPurpose/Create';
import FirearmPurposeEdit from '../pages/admin/firearmPurpose/Edit';

import FirearmLooseBarrelTypeList from '../pages/admin/firearmLooseBarrelType/List';
import FirearmLooseBarrelTypeCreate from '../pages/admin/firearmLooseBarrelType/Create';
import FirearmLooseBarrelTypeEdit from '../pages/admin/firearmLooseBarrelType/Edit';

import FirearmCompetencyTypeList from '../pages/admin/firearmCompetencyType/List';
import FirearmCompetencyTypeCreate from '../pages/admin/firearmCompetencyType/Create';
import FirearmCompetencyTypeEdit from '../pages/admin/firearmCompetencyType/Edit';

import MemberTypeList from '../pages/admin/memberType/List';
import MemberTypeCreate from '../pages/admin/memberType/Create';
import MemberTypeEdit from '../pages/admin/memberType/Edit';

import LanguageList from '../pages/admin/language/List';
import LanguageCreate from '../pages/admin/language/Create';
import LanguageEdit from '../pages/admin/language/Edit';

import ProvinceList from '../pages/admin/province/List';
import ProvinceCreate from '../pages/admin/province/Create';
import ProvinceEdit from '../pages/admin/province/Edit';
import FirearmOwnerList from '../pages/admin/firearmOwner/List';
import FirearmOwnerCreate from '../pages/admin/firearmOwner/Create';
import FirearmOwnerEdit from '../pages/admin/firearmOwner/Edit';

import ActivityTypeList from '../pages/admin/activityType/List';
import ActivityTypeCreate from '../pages/admin/activityType/Create';
import ActivityTypeEdit from '../pages/admin/activityType/Edit';

import ActivityCategoryList from '../pages/admin/activityCategory/List';
import ActivityCategoryCreate from '../pages/admin/activityCategory/Create';
import ActivityCategoryEdit from '../pages/admin/activityCategory/Edit';

import QuickEventList from '../pages/admin/quickEvent/List';
import QuickEventCreate from '../pages/admin/quickEvent/Create';
import QuickEventEdit from '../pages/admin/quickEvent/Edit';

import ProfileEdit from '../pages/admin/profile/Edit';

import EndorsementMotivationList from '../pages/admin/endorsementMotivation/List';
import EndorsementMotivationCreate from '../pages/admin/endorsementMotivation/Create';
import EndorsementMotivationEdit from '../pages/admin/endorsementMotivation/Edit';

import DocumentTypeList from '../pages/admin/documentType/List';
import DocumentTypeCreate from '../pages/admin/documentType/Create';
import DocumentTypeEdit from '../pages/admin/documentType/Edit';

import MessageTemplateStatusList from '../pages/admin/messageTemplateStatus/List';
import MessageTemplateStatusCreate from '../pages/admin/messageTemplateStatus/Create';
import MessageTemplateStatusEdit from '../pages/admin/messageTemplateStatus/Edit';

import NewsletterCategoryList from '../pages/admin/newsletterCategory/List';
import NewsletterCategoryCreate from '../pages/admin/newsletterCategory/Create';
import NewsletterCategoryEdit from '../pages/admin/newsletterCategory/Edit';

import MemberCancelReasonList from '../pages/admin/memberCancelReason/List';
import MemberCancelReasonCreate from '../pages/admin/memberCancelReason/Create';
import MemberCancelReasonEdit from '../pages/admin/memberCancelReason/Edit';

import CompetitionClassTypeList from '../pages/admin/competitionClassType/List';
import CompetitionClassTypeCreate from '../pages/admin/competitionClassType/Create';
import CompetitionClassTypeEdit from '../pages/admin/competitionClassType/Edit';

import CourseMaterialTypeList from '../pages/admin/courseMaterialType/List';
import CourseMaterialTypeCreate from '../pages/admin/courseMaterialType/Create';
import CourseMaterialTypeEdit from '../pages/admin/courseMaterialType/Edit';

import EndorsementLicenceTypeList from '../pages/admin/endorsementLicenceType/List';
import EndorsementLicenceTypeCreate from '../pages/admin/endorsementLicenceType/Create';
import EndorsementLicenceTypeEdit from '../pages/admin/endorsementLicenceType/Edit';

import MemberDisciplinaryStatusList from '../pages/admin/memberDisciplinaryStatus/List';
import MemberDisciplinaryStatusCreate from '../pages/admin/memberDisciplinaryStatus/Create';
import MemberDisciplinaryStatusEdit from '../pages/admin/memberDisciplinaryStatus/Edit';

import PaymentIntervalList from '../pages/admin/paymentInterval/List';
import PaymentIntervalCreate from '../pages/admin/paymentInterval/Create';
import PaymentIntervalEdit from '../pages/admin/paymentInterval/Edit';

import MemberIdTypeList from '../pages/admin/memberIdType/List';
import MemberIdTypeCreate from '../pages/admin/memberIdType/Create';
import MemberIdTypeEdit from '../pages/admin/memberIdType/Edit';

import ColourGradingList from '../pages/admin/colourGrading/List';
import ColourGradingCreate from '../pages/admin/colourGrading/Create';
import ColourGradingEdit from '../pages/admin/colourGrading/Edit';

import MemberReferralTypeList from '../pages/admin/memberReferralType/List';
import MemberReferralTypeCreate from '../pages/admin/memberReferralType/Create';
import MemberReferralTypeEdit from '../pages/admin/memberReferralType/Edit';

import MemberRegistrationTypeList from '../pages/admin/memberRegistrationType/List';
import MemberRegistrationTypeCreate from '../pages/admin/memberRegistrationType/Create';
import MemberRegistrationTypeEdit from '../pages/admin/memberRegistrationType/Edit';

import FirearmSerialTypeList from '../pages/admin/firearmSerialType/List';
import FirearmSerialTypeCreate from '../pages/admin/firearmSerialType/Create';
import FirearmSerialTypeEdit from '../pages/admin/firearmSerialType/Edit';

import MemberTerminationStatusList from '../pages/admin/memberTerminationStatus/List';
import MemberTerminationStatusCreate from '../pages/admin/memberTerminationStatus/Create';
import MemberTerminationStatusEdit from '../pages/admin/memberTerminationStatus/Edit';

import MemberTitleList from '../pages/admin/memberTitle/List';
import MemberTitleCreate from '../pages/admin/memberTitle/Create';
import MemberTitleEdit from '../pages/admin/memberTitle/Edit';

import CompetitionClassList from '../pages/admin/competitionClass/List';
import CompetitionClassCreate from '../pages/admin/competitionClass/Create';
import CompetitionClassEdit from '../pages/admin/competitionClass/Edit';

import MemberCategoryList from '../pages/admin/memberCategory/List';
import MemberCategoryCreate from '../pages/admin/memberCategory/Create';
import MemberCategoryEdit from '../pages/admin/memberCategory/Edit';
import VatRateHistoryList from '../pages/admin/vatRateHistory/List';
import VatRateHistoryCreate from '../pages/admin/vatRateHistory/Create';
import VatRateHistoryEdit from '../pages/admin/vatRateHistory/Edit';

import CompetitionExerciseTypeList from '../pages/admin/competitionExerciseType/List';
import CompetitionExerciseTypeCreate from '../pages/admin/competitionExerciseType/Create';
import CompetitionExerciseTypeEdit from '../pages/admin/competitionExerciseType/Edit';

import RenewalStatusList from '../pages/admin/renewalStatus/List';
import RenewalStatusCreate from '../pages/admin/renewalStatus/Create';
import RenewalStatusEdit from '../pages/admin/renewalStatus/Edit';

import RplStatusList from '../pages/admin/rplStatus/List';
import RplStatusCreate from '../pages/admin/rplStatus/Create';
import RplStatusEdit from '../pages/admin/rplStatus/Edit';

import RangeStatusList from '../pages/admin/rangeStatus/List';
import RangeStatusCreate from '../pages/admin/rangeStatus/Create';
import RangeStatusEdit from '../pages/admin/rangeStatus/Edit';

import RangeShootingReasonList from '../pages/admin/rangeShootingReason/List';
import RangeShootingReasonCreate from '../pages/admin/rangeShootingReason/Create';
import RangeShootingReasonEdit from '../pages/admin/rangeShootingReason/Edit';

import MemberRegistrationStepList from '../pages/admin/memberRegistrationStep/List';
import MemberRegistrationStepCreate from '../pages/admin/memberRegistrationStep/Create';
import MemberRegistrationStepEdit from '../pages/admin/memberRegistrationStep/Edit';

import EvaluationStatusList from '../pages/admin/evaluationStatus/List';
import EvaluationStatusCreate from '../pages/admin/evaluationStatus/Create';
import EvaluationStatusEdit from '../pages/admin/evaluationStatus/Edit';

import FarmStatusList from '../pages/admin/farmStatus/List';
import FarmStatusCreate from '../pages/admin/farmStatus/Create';
import FarmStatusEdit from '../pages/admin/farmStatus/Edit';

import InvoiceStatusList from '../pages/admin/invoiceStatus/List';
import InvoiceStatusCreate from '../pages/admin/invoiceStatus/Create';
import InvoiceStatusEdit from '../pages/admin/invoiceStatus/Edit';

import ScoreStatusList from '../pages/admin/scoreStatus/List';
import ScoreStatusCreate from '../pages/admin/scoreStatus/Create';
import ScoreStatusEdit from '../pages/admin/scoreStatus/Edit';

import CompetitionStatusList from '../pages/admin/competitionStatus/List';
import CompetitionStatusCreate from '../pages/admin/competitionStatus/Create';
import CompetitionStatusEdit from '../pages/admin/competitionStatus/Edit';

import EndorsementStatusList from '../pages/admin/endorsementStatus/List';
import EndorsementStatusCreate from '../pages/admin/endorsementStatus/Create';
import EndorsementStatusEdit from '../pages/admin/endorsementStatus/Edit';

import CourseStatusList from '../pages/admin/courseStatus/List';
import CourseStatusCreate from '../pages/admin/courseStatus/Create';
import CourseStatusEdit from '../pages/admin/courseStatus/Edit';

import MemberStatusList from '../pages/admin/memberStatus/List';
import MemberStatusCreate from '../pages/admin/memberStatus/Create';
import MemberStatusEdit from '../pages/admin/memberStatus/Edit';

import ActivityStatusList from '../pages/admin/activityStatus/List';
import ActivityStatusCreate from '../pages/admin/activityStatus/Create';
import ActivityStatusEdit from '../pages/admin/activityStatus/Edit';

import ComponentActionList from '../pages/admin/componentAction/List';
import ComponentActionCreate from '../pages/admin/componentAction/Create';
import ComponentActionEdit from '../pages/admin/componentAction/Edit';

import ReplyTemplateList from '../pages/admin/replyTemplate/List';
import ReplyTemplateCreate from '../pages/admin/replyTemplate/Create';
import ReplyTemplateEdit from '../pages/admin/replyTemplate/Edit';

import RangeTypeList from '../pages/admin/rangeType/List';
import RangeTypeCreate from '../pages/admin/rangeType/Create';
import RangeTypeEdit from '../pages/admin/rangeType/Edit';

import RangeList from '../pages/admin/range/List';
import RangeCreate from '../pages/admin/range/Create';
import RangeEdit from '../pages/admin/range/Edit';

import ListingTypeList from '../pages/admin/listingType/List';
import ListingTypeCreate from '../pages/admin/listingType/Create';
import ListingTypeEdit from '../pages/admin/listingType/Edit';

import ListingStatusList from '../pages/admin/listingStatus/List';
import ListingStatusCreate from '../pages/admin/listingStatus/Create';
import ListingStatusEdit from '../pages/admin/listingStatus/Edit';

import GenderList from '../pages/admin/gender/List';
import GenderCreate from '../pages/admin/gender/Create';
import GenderEdit from '../pages/admin/gender/Edit';

import CountryList from '../pages/admin/country/List';
import CountryCreate from '../pages/admin/country/Create';
import CountryEdit from '../pages/admin/country/Edit';

import MemberList from '../pages/admin/member/List';
import MemberCreate from '../pages/admin/member/Create';
import MemberEdit from '../pages/admin/member/Edit';

import ListingList from '../pages/admin/listing/List';
import ListingCreate from '../pages/admin/listing/Create';
import ListingEdit from '../pages/admin/listing/Edit';

import QuestionSetList from '../pages/admin/questionSet/List';
import QuestionSetCreate from '../pages/admin/questionSet/Create';
import QuestionSetEdit from '../pages/admin/questionSet/Edit';

import QuestionCreate from '../pages/admin/question/Create';
import QuestionEdit from '../pages/admin/question/Edit';

import NoteTypeList from '../pages/admin/noteType/List';
import NoteTypeCreate from '../pages/admin/noteType/Create';
import NoteTypeEdit from '../pages/admin/noteType/Edit';

import ReplyTypeList from '../pages/admin/replyType/List';
import ReplyTypeCreate from '../pages/admin/replyType/Create';
import ReplyTypeEdit from '../pages/admin/replyType/Edit';

import InvoiceTypeList from '../pages/admin/invoiceType/List';
import InvoiceTypeCreate from '../pages/admin/invoiceType/Create';
import InvoiceTypeEdit from '../pages/admin/invoiceType/Edit';

import PaymentTypeList from '../pages/admin/paymentType/List';
import PaymentTypeCreate from '../pages/admin/paymentType/Create';
import PaymentTypeEdit from '../pages/admin/paymentType/Edit';

import NewsletterList from '../pages/admin/newsletter/List';
import NewsletterCreate from '../pages/admin/newsletter/Create';
import NewsletterEdit from '../pages/admin/newsletter/Edit';

import FarmTypeList from '../pages/admin/farmType/List';
import FarmTypeCreate from '../pages/admin/farmType/Create';
import FarmTypeEdit from '../pages/admin/farmType/Edit';

import CertificateTypeList from '../pages/admin/certificateType/List';
import CertificateTypeCreate from '../pages/admin/certificateType/Create';
import CertificateTypeEdit from '../pages/admin/certificateType/Edit';
import PagesList from '../pages/admin/pages/List';
import PagesCreate from '../pages/admin/pages/Create';
import PagesEdit from '../pages/admin/pages/Edit';

import CompetencyTypeList from '../pages/admin/competencyType/List';
import CompetencyTypeCreate from '../pages/admin/competencyType/Create';
import CompetencyTypeEdit from '../pages/admin/competencyType/Edit';
import ScoreTypeList from '../pages/admin/scoreType/List';
import ScoreTypeCreate from '../pages/admin/scoreType/Create';
import ScoreTypeEdit from '../pages/admin/scoreType/Edit';

import ReconTypeList from '../pages/admin/reconType/List';
import ReconTypeCreate from '../pages/admin/reconType/Create';
import ReconTypeEdit from '../pages/admin/reconType/Edit';

import CourseTypeList from '../pages/admin/courseType/List';
import CourseTypeCreate from '../pages/admin/courseType/Create';
import CourseTypeEdit from '../pages/admin/courseType/Edit';

import ModuleTypeList from '../pages/admin/moduleType/List';
import ModuleTypeCreate from '../pages/admin/moduleType/Create';
import ModuleTypeEdit from '../pages/admin/moduleType/Edit';

import EnquiryTypeList from '../pages/admin/enquiryType/List';
import EnquiryTypeCreate from '../pages/admin/enquiryType/Create';
import EnquiryTypeEdit from '../pages/admin/enquiryType/Edit';

import EmailTypeList from '../pages/admin/emailType/List';
import EmailTypeCreate from '../pages/admin/emailType/Create';
import EmailTypeEdit from '../pages/admin/emailType/Edit';

import ComponentList from '../pages/admin/component/List';
import ComponentCreate from '../pages/admin/component/Create';
import ComponentEdit from '../pages/admin/component/Edit';

import ExerciseTemplateList from '../pages/admin/exerciseTemplate/List';
import ExerciseTemplateCreate from '../pages/admin/exerciseTemplate/Create';
import ExerciseTemplateEdit from '../pages/admin/exerciseTemplate/Edit';

import MemberCompetencyList from '../pages/admin/memberCompetency/List';
import MemberCompetencyCreate from '../pages/admin/memberCompetency/Create';
import MemberCompetencyEdit from '../pages/admin/memberCompetency/Edit';

import MemberFirearmList from '../pages/admin/memberFirearm/List';
import MemberFirearmCreate from '../pages/admin/memberFirearm/Create';
import MemberFirearmEdit from '../pages/admin/memberFirearm/Edit';

import MemberEndorsementList from '../pages/admin/memberEndorsement/List';
import MemberEndorsementCreate from '../pages/admin/memberEndorsement/Create';
import MemberEndorsementEdit from '../pages/admin/memberEndorsement/Edit';
import VerifyCertificate from '../pages/public/member/Verify';
import MemberSetup from '../pages/admin/MemberSetup';
import PayfastReturn from '../pages/public/payment/PayfastReturn';
import PartnerServiceRedirect from '../pages/public/PartnerServiceRedirect';

import CompetitionList from '../pages/admin/competition/List';
import CompetitionCreate from '../pages/admin/competition/Create';
import CompetitionEdit from '../pages/admin/competition/Edit';

import InvoiceList from '../pages/admin/invoice/List';
import InvoiceCreate from '../pages/admin/invoice/Create';
import InvoiceEdit from '../pages/admin/invoice/Edit';

import InvoiceLineItemList from '../pages/admin/invoiceLineItem/List';
import InvoiceLineItemCreate from '../pages/admin/invoiceLineItem/Create';
import InvoiceLineItemEdit from '../pages/admin/invoiceLineItem/Edit';

import FinanceExport from '../pages/admin/reports/FinanceExport';
import CourseDuplicateExamineeExport from '../pages/admin/reports/CourseDuplicateExamineeExport';
import LifeMembersExport from '../pages/admin/reports/LifeMembersExport';

import ActivityList from '../pages/admin/activity/List';
import ActivityCreate from '../pages/admin/activity/Create';
import ActivityEdit from '../pages/admin/activity/Edit';

import ActivitySpeciesList from '../pages/admin/activitySpeciesType/List';
import ActivitySpeciesCreate from '../pages/admin/activitySpeciesType/Create';
import ActivitySpeciesEdit from '../pages/admin/activitySpeciesType/Edit';

import RplList from '../pages/admin/rpl/List';
import RplCreate from '../pages/admin/rpl/Create';
import RplEdit from '../pages/admin/rpl/Edit';
import DedicatedStatus from '../pages/admin/dedicatedstatus/DedicatedStatus';

import CourseList from '../pages/admin/course/List';
import CourseCreate from '../pages/admin/course/Create';
import CourseEdit from '../pages/admin/course/Edit';

import ScoreList from '../pages/admin/score/List';
import ScoreCreate from '../pages/admin/score/Create';
import ScoreEdit from '../pages/admin/score/Edit';
import Evaluation from '../pages/admin/course/Evaluation';
import Documents from '../pages/admin/Documents';
import VerifyEmail from '../pages/public/VerifyEmail';
import VerifyCertificateForm from '../pages/public/VerifyCertificateForm';
import PaymentRedirect from '../pages/public/payment/PaymentRedirect';
import PaymentConfirmation from '../pages/public/payment/PaymentConfirmation';
import ImpersonationRedirect from '../pages/admin/impersonation/ImpersonationRedirect';

import EmailMessageThreadList from '../pages/admin/emailMessageThread/List';
import EmailMessageThreadCreate from '../pages/admin/emailMessageThread/Create';
import EmailMessageThreadEdit from '../pages/admin/emailMessageThread/Edit';

import InvoiceBatchList from '../pages/admin/invoiceBatch/List';
import InvoiceBatchDetail from '../pages/admin/invoiceBatch/Detail';

import MemberFeeList from '../pages/admin/memberFee/List';
import MemberFeeCreate from '../pages/admin/memberFee/Create';
import MemberFeeEdit from '../pages/admin/memberFee/Edit';

import NoteList from '../pages/admin/note/List';
import NoteCreate from '../pages/admin/note/Create';
import NoteEdit from '../pages/admin/note/Edit';

import ReconTransactionList from '../pages/admin/reconTransaction/List';
import EnquiryList from '../pages/admin/enquiry/List';
import EnquiryCreate from '../pages/admin/enquiry/Create';
import EnquiryEdit from '../pages/admin/enquiry/Edit';

import CompetitionEntryList from '../pages/admin/competitionEntry/List';
import CompetitionResultList from '../pages/admin/competitionResult/List';
import CompetitionResultCreate from '../pages/admin/competitionResult/Create';
import CompetitionResultEdit from '../pages/admin/competitionResult/Edit';
import CompetitionResultExercises from '../pages/admin/competitionResult/Exercises';
import CompetitionResultEntries from '../pages/admin/competitionResult/Entries';
import CompetitionResultMyResults from '../pages/admin/competitionResult/MyResults';
import CompetitionEntryCreate from '../pages/admin/competitionEntry/Create';
import CompetitionEntryEdit from '../pages/admin/competitionEntry/Edit';
import MemberProfileEdit from '../pages/admin/memberProfile/Edit';

import MemberAdditionalDocumentList from '../pages/admin/memberAdditionalDocument/List';
import MemberAdditionalDocumentCreate from '../pages/admin/memberAdditionalDocument/Create';
import MemberAdditionalDocumentEdit from '../pages/admin/memberAdditionalDocument/Edit';
import QuickEventView from '../pages/public/QuickEventsView';

import FriendAndPartnerList from '../pages/admin/friendAndPartner/List';
import FriendAndPartnerCreate from '../pages/admin/friendAndPartner/Create';
import FriendAndPartnerEdit from '../pages/admin/friendAndPartner/Edit';

import FellowList from '../pages/admin/fellow/List';
import FellowCreate from '../pages/admin/fellow/Create';
import FellowEdit from '../pages/admin/fellow/Edit';

import ForfeitureReasonList from '../pages/admin/forfeitureReason/List';
import ForfeitureReasonCreate from '../pages/admin/forfeitureReason/Create';
import ForfeitureReasonEdit from '../pages/admin/forfeitureReason/Edit';

import ContentCategoryList from '../pages/admin/contentCategory/List';
import ContentCategoryCreate from '../pages/admin/contentCategory/Create';
import ContentCategoryEdit from '../pages/admin/contentCategory/Edit';

import ArticleList from '../pages/admin/article/List';
import ArticleCreate from '../pages/admin/article/Create';
import ArticleEdit from '../pages/admin/article/Edit';

import NewsItemList from '../pages/admin/newsItem/List';
import NewsItemCreate from '../pages/admin/newsItem/Create';
import NewsItemEdit from '../pages/admin/newsItem/Edit';
import CompetitionResultEntryList from '../pages/admin/competitionResultEntry/List';
import CompetitionResultEntryCreate from '../pages/admin/competitionResultEntry/Create';
import CompetitionResultEntryEdit from '../pages/admin/competitionResultEntry/Edit';

// Import route here

// Component to track page views on route changes
const PageTracker = (): null => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
};

// `/admin` has no page of its own: send the signed-in user to the first admin screen they may see.
const AdminLanding = (): JSX.Element => (
  <Navigate to={getFirstAvailableRoute(getPermissions() || [])} replace />
);

const AppRouter = (): JSX.Element => (
  <ViewProvider>
    {' '}
    {/* Wrap all routes in the ViewProvider */}
    <Router>
      <PageTracker />
      <Routes>
        {/* The portal root is the admin area. Signed out, the `/admin` guard sends you to /login,
            and after login you come back to `/admin`. */}
        <Route path="/" element={<Navigate to="/admin" replace />} />
        <Route
          path="/admin"
          element={
            <PrivateRoute>
              <AdminLanding />
            </PrivateRoute>
          }
        />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/cancel-application" element={<CancelApplication />} />
        <Route path="/rating/:threadId/:rating" element={<RatingThankYou />} />
        <Route path="/join" element={<Join />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/upload-proof" element={<ProofOfPaymentPage />} />
        <Route path="/register-range" element={<PublicRangeCreate />} />
        <Route path="/register-listing" element={<PublicListingCreate />} />
        <Route path="/listings" element={<ListingSearch />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/verify-certificate" element={<VerifyCertificateForm />} />
        <Route path="/payment/redirect" element={<PaymentRedirect />} />
        <Route path="/payment/confirmation" element={<PaymentConfirmation />} />
        <Route path="/debit-order-mandate" element={<DebitOrderMandateForm />} />

        <Route path="/member/verify/:memberid/*" element={<VerifyCertificate />} />
        <Route path="/member/verify/:memberid/:type/*" element={<VerifyCertificate />} />
        <Route path="/member/verify/:memberid/:type/:typeId" element={<VerifyCertificate />} />

        <Route path="/payment/return" element={<PayfastReturn />} />
        <Route path="/partner-redirect/:partnerId/:memberId" element={<PartnerServiceRedirect />} />
        <Route path="/quickevents/view/:id" element={<QuickEventView />} />

        {/* Info hub (landing + rendered content pages). Its landing is `/content`.
            It does NOT own the app root — `/` goes to `/admin` (see above).
            The module owns its own route tree — see modules/infoHub/README.md.

            MEMBERS ONLY — team decision 2026-08-09. The hub is not public and is not indexed;
            it is part of the portal but sits as its own area with its own shell. The guard is
            applied here rather than inside the module on purpose: `PrivateRoute` is host app
            code, and the module's ESLint boundary forbids it from importing any (it reaches the
            app through `api/infoHubApi.ts` alone). A pathless parent route keeps the module's
            tree untouched and liftable — the guard is the host's to supply.

            Because the parent has no `path`, the child routes keep their absolute paths. The
            public front door is the Astro one-pager on natshoot.co.za, a different origin. */}
        <Route
          element={
            <PrivateRoute>
              <Outlet />
            </PrivateRoute>
          }
        >
          {infoHubRoutes}
        </Route>

        {/* Protected admin routes */}
        <Route
          path="/admin/documents"
          element={
            <PrivateRoute>
              <Documents />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/dashboard"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/impersonate/:memberId"
          element={
            <PrivateRoute>
              <ImpersonationRedirect />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/auditlogs"
          element={
            <PrivateRoute>
              <AuditLogList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/companyinfo"
          element={
            <PrivateRoute>
              <CompanyInfoList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/companyinfo/create"
          element={
            <PrivateRoute>
              <CompanyInfoCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/companyinfo/:id"
          element={
            <PrivateRoute>
              <CompanyInfoEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/bulkemails"
          element={
            <PrivateRoute>
              <BulkEmailList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/bulkemails/create"
          element={
            <PrivateRoute>
              <BulkEmailCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/bulkemails/:id"
          element={
            <PrivateRoute>
              <BulkEmailEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/users"
          element={
            <PrivateRoute>
              <UsersList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/users/create"
          element={
            <PrivateRoute>
              <UserCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/users/:id"
          element={
            <PrivateRoute>
              <UserEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/profile"
          element={
            <PrivateRoute>
              <ProfileEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/member-profile"
          element={
            <PrivateRoute>
              <MemberProfileEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/firearmtypes"
          element={
            <PrivateRoute>
              <FirearmTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmtypes/create"
          element={
            <PrivateRoute>
              <FirearmTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmtypes/:id"
          element={
            <PrivateRoute>
              <FirearmTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/firearmspecifics"
          element={
            <PrivateRoute>
              <FirearmSpecificList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmspecifics/create"
          element={
            <PrivateRoute>
              <FirearmSpecificCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmspecifics/:id"
          element={
            <PrivateRoute>
              <FirearmSpecificEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/firearmmakes"
          element={
            <PrivateRoute>
              <FirearmMakeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmmakes/create"
          element={
            <PrivateRoute>
              <FirearmMakeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmmakes/:id"
          element={
            <PrivateRoute>
              <FirearmMakeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/gradingtypes"
          element={
            <PrivateRoute>
              <GradingTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/gradingtypes/create"
          element={
            <PrivateRoute>
              <GradingTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/gradingtypes/:id"
          element={
            <PrivateRoute>
              <GradingTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/gradingcategories"
          element={
            <PrivateRoute>
              <GradingCategoryList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/gradingcategories/create"
          element={
            <PrivateRoute>
              <GradingCategoryCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/gradingcategories/:id"
          element={
            <PrivateRoute>
              <GradingCategoryEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/exercisetypes"
          element={
            <PrivateRoute>
              <ExerciseTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/exercisetypes/create"
          element={
            <PrivateRoute>
              <ExerciseTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/exercisetypes/:id"
          element={
            <PrivateRoute>
              <ExerciseTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/exerciseactivitytargets"
          element={
            <PrivateRoute>
              <ExerciseActivityTargetList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/exerciseactivitytargets/create"
          element={
            <PrivateRoute>
              <ExerciseActivityTargetCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/exerciseactivitytargets/:id"
          element={
            <PrivateRoute>
              <ExerciseActivityTargetEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/exercisetargets"
          element={
            <PrivateRoute>
              <ExerciseTargetList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/exercisetargets/create"
          element={
            <PrivateRoute>
              <ExerciseTargetCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/exercisetargets/:id"
          element={
            <PrivateRoute>
              <ExerciseTargetEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/exercisespecifics"
          element={
            <PrivateRoute>
              <ExerciseSpecificList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/exercisespecifics/create"
          element={
            <PrivateRoute>
              <ExerciseSpecificCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/exercisespecifics/:id"
          element={
            <PrivateRoute>
              <ExerciseSpecificEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/firearmtypes"
          element={
            <PrivateRoute>
              <FirearmTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmtypes/create"
          element={
            <PrivateRoute>
              <FirearmTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmtypes/:id"
          element={
            <PrivateRoute>
              <FirearmTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/firearmspecifics"
          element={
            <PrivateRoute>
              <FirearmSpecificList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmspecifics/create"
          element={
            <PrivateRoute>
              <FirearmSpecificCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmspecifics/:id"
          element={
            <PrivateRoute>
              <FirearmSpecificEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/firearmmakes"
          element={
            <PrivateRoute>
              <FirearmMakeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmmakes/create"
          element={
            <PrivateRoute>
              <FirearmMakeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmmakes/:id"
          element={
            <PrivateRoute>
              <FirearmMakeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/firearmbarrelmakes"
          element={
            <PrivateRoute>
              <FirearmBarrelMakeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmbarrelmakes/create"
          element={
            <PrivateRoute>
              <FirearmBarrelMakeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmbarrelmakes/:id"
          element={
            <PrivateRoute>
              <FirearmBarrelMakeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/firearmcalibres"
          element={
            <PrivateRoute>
              <FirearmCalibreList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmcalibres/create"
          element={
            <PrivateRoute>
              <FirearmCalibreCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmcalibres/:id"
          element={
            <PrivateRoute>
              <FirearmCalibreEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/firearmbarrels"
          element={
            <PrivateRoute>
              <FirearmBarrelList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmbarrels/create"
          element={
            <PrivateRoute>
              <FirearmBarrelCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmbarrels/:id"
          element={
            <PrivateRoute>
              <FirearmBarrelEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/firearmactions"
          element={
            <PrivateRoute>
              <FirearmActionList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmactions/create"
          element={
            <PrivateRoute>
              <FirearmActionCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmactions/:id"
          element={
            <PrivateRoute>
              <FirearmActionEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/firearmpurposes"
          element={
            <PrivateRoute>
              <FirearmPurposeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmpurposes/create"
          element={
            <PrivateRoute>
              <FirearmPurposeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmpurposes/:id"
          element={
            <PrivateRoute>
              <FirearmPurposeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/firearmloosebarreltypes"
          element={
            <PrivateRoute>
              <FirearmLooseBarrelTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmloosebarreltypes/create"
          element={
            <PrivateRoute>
              <FirearmLooseBarrelTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmloosebarreltypes/:id"
          element={
            <PrivateRoute>
              <FirearmLooseBarrelTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/firearmcompetencytypes"
          element={
            <PrivateRoute>
              <FirearmCompetencyTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmcompetencytypes/create"
          element={
            <PrivateRoute>
              <FirearmCompetencyTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmcompetencytypes/:id"
          element={
            <PrivateRoute>
              <FirearmCompetencyTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/membertypes"
          element={
            <PrivateRoute>
              <MemberTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/membertypes/create"
          element={
            <PrivateRoute>
              <MemberTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/membertypes/:id"
          element={
            <PrivateRoute>
              <MemberTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/languages"
          element={
            <PrivateRoute>
              <LanguageList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/languages/create"
          element={
            <PrivateRoute>
              <LanguageCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/languages/:id"
          element={
            <PrivateRoute>
              <LanguageEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/provinces"
          element={
            <PrivateRoute>
              <ProvinceList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/provinces/create"
          element={
            <PrivateRoute>
              <ProvinceCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/provinces/:id"
          element={
            <PrivateRoute>
              <ProvinceEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/activitytypes"
          element={
            <PrivateRoute>
              <ActivityTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/activitytypes/create"
          element={
            <PrivateRoute>
              <ActivityTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/activitytypes/:id"
          element={
            <PrivateRoute>
              <ActivityTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/activitycategories"
          element={
            <PrivateRoute>
              <ActivityCategoryList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/activitycategories/create"
          element={
            <PrivateRoute>
              <ActivityCategoryCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/activitycategories/:id"
          element={
            <PrivateRoute>
              <ActivityCategoryEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/firearmowners"
          element={
            <PrivateRoute>
              <FirearmOwnerList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmowners/create"
          element={
            <PrivateRoute>
              <FirearmOwnerCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmowners/:id"
          element={
            <PrivateRoute>
              <FirearmOwnerEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/quickevents"
          element={
            <PrivateRoute>
              <QuickEventList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/quickevents/create"
          element={
            <PrivateRoute>
              <QuickEventCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/quickevents/:id"
          element={
            <PrivateRoute>
              <QuickEventEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/endorsementmotivations"
          element={
            <PrivateRoute>
              <EndorsementMotivationList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/endorsementmotivations/create"
          element={
            <PrivateRoute>
              <EndorsementMotivationCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/endorsementmotivations/:id"
          element={
            <PrivateRoute>
              <EndorsementMotivationEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/documenttypes"
          element={
            <PrivateRoute>
              <DocumentTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/documenttypes/create"
          element={
            <PrivateRoute>
              <DocumentTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/documenttypes/:id"
          element={
            <PrivateRoute>
              <DocumentTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/messagetemplatestatuses"
          element={
            <PrivateRoute>
              <MessageTemplateStatusList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/messagetemplatestatuses/create"
          element={
            <PrivateRoute>
              <MessageTemplateStatusCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/messagetemplatestatuses/:id"
          element={
            <PrivateRoute>
              <MessageTemplateStatusEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/newslettercategories"
          element={
            <PrivateRoute>
              <NewsletterCategoryList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/newslettercategories/create"
          element={
            <PrivateRoute>
              <NewsletterCategoryCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/newslettercategories/:id"
          element={
            <PrivateRoute>
              <NewsletterCategoryEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/membercancelreasons"
          element={
            <PrivateRoute>
              <MemberCancelReasonList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/membercancelreasons/create"
          element={
            <PrivateRoute>
              <MemberCancelReasonCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/membercancelreasons/:id"
          element={
            <PrivateRoute>
              <MemberCancelReasonEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/competitionclasstypes"
          element={
            <PrivateRoute>
              <CompetitionClassTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/competitionclasstypes/create"
          element={
            <PrivateRoute>
              <CompetitionClassTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/competitionclasstypes/:id"
          element={
            <PrivateRoute>
              <CompetitionClassTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/coursematerialtypes"
          element={
            <PrivateRoute>
              <CourseMaterialTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/coursematerialtypes/create"
          element={
            <PrivateRoute>
              <CourseMaterialTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/coursematerialtypes/:id"
          element={
            <PrivateRoute>
              <CourseMaterialTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/endorsementlicencetypes"
          element={
            <PrivateRoute>
              <EndorsementLicenceTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/endorsementlicencetypes/create"
          element={
            <PrivateRoute>
              <EndorsementLicenceTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/endorsementlicencetypes/:id"
          element={
            <PrivateRoute>
              <EndorsementLicenceTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/memberdisciplinarystatuses"
          element={
            <PrivateRoute>
              <MemberDisciplinaryStatusList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberdisciplinarystatuses/create"
          element={
            <PrivateRoute>
              <MemberDisciplinaryStatusCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberdisciplinarystatuses/:id"
          element={
            <PrivateRoute>
              <MemberDisciplinaryStatusEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/paymentintervals"
          element={
            <PrivateRoute>
              <PaymentIntervalList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/paymentintervals/create"
          element={
            <PrivateRoute>
              <PaymentIntervalCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/paymentintervals/:id"
          element={
            <PrivateRoute>
              <PaymentIntervalEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/memberidtypes"
          element={
            <PrivateRoute>
              <MemberIdTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberidtypes/create"
          element={
            <PrivateRoute>
              <MemberIdTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberidtypes/:id"
          element={
            <PrivateRoute>
              <MemberIdTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/colourgradings"
          element={
            <PrivateRoute>
              <ColourGradingList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/colourgradings/create"
          element={
            <PrivateRoute>
              <ColourGradingCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/colourgradings/:id"
          element={
            <PrivateRoute>
              <ColourGradingEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/memberreferraltypes"
          element={
            <PrivateRoute>
              <MemberReferralTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberreferraltypes/create"
          element={
            <PrivateRoute>
              <MemberReferralTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberreferraltypes/:id"
          element={
            <PrivateRoute>
              <MemberReferralTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/memberregistrationtypes"
          element={
            <PrivateRoute>
              <MemberRegistrationTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberregistrationtypes/create"
          element={
            <PrivateRoute>
              <MemberRegistrationTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberregistrationtypes/:id"
          element={
            <PrivateRoute>
              <MemberRegistrationTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/firearmserialtypes"
          element={
            <PrivateRoute>
              <FirearmSerialTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmserialtypes/create"
          element={
            <PrivateRoute>
              <FirearmSerialTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/firearmserialtypes/:id"
          element={
            <PrivateRoute>
              <FirearmSerialTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/memberterminationstatuses"
          element={
            <PrivateRoute>
              <MemberTerminationStatusList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberterminationstatuses/create"
          element={
            <PrivateRoute>
              <MemberTerminationStatusCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberterminationstatuses/:id"
          element={
            <PrivateRoute>
              <MemberTerminationStatusEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/membertitles"
          element={
            <PrivateRoute>
              <MemberTitleList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/membertitles/create"
          element={
            <PrivateRoute>
              <MemberTitleCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/membertitles/:id"
          element={
            <PrivateRoute>
              <MemberTitleEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/competitionclasses"
          element={
            <PrivateRoute>
              <CompetitionClassList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/competitionclasses/create"
          element={
            <PrivateRoute>
              <CompetitionClassCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/competitionclasses/:id"
          element={
            <PrivateRoute>
              <CompetitionClassEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/membercategories"
          element={
            <PrivateRoute>
              <MemberCategoryList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/membercategories/create"
          element={
            <PrivateRoute>
              <MemberCategoryCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/membercategories/:id"
          element={
            <PrivateRoute>
              <MemberCategoryEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/vatratehistories"
          element={
            <PrivateRoute>
              <VatRateHistoryList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/vatratehistories/create"
          element={
            <PrivateRoute>
              <VatRateHistoryCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/vatratehistories/:id"
          element={
            <PrivateRoute>
              <VatRateHistoryEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/competitionexercisetypes"
          element={
            <PrivateRoute>
              <CompetitionExerciseTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/competitionexercisetypes/create"
          element={
            <PrivateRoute>
              <CompetitionExerciseTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/competitionexercisetypes/:id"
          element={
            <PrivateRoute>
              <CompetitionExerciseTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/renewalstatuses"
          element={
            <PrivateRoute>
              <RenewalStatusList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/renewalstatuses/create"
          element={
            <PrivateRoute>
              <RenewalStatusCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/renewalstatuses/:id"
          element={
            <PrivateRoute>
              <RenewalStatusEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/activityspeciestype"
          element={
            <PrivateRoute>
              <ActivityTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/activityspeciestype/create"
          element={
            <PrivateRoute>
              <ActivityTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/activityspeciestype/:id"
          element={
            <PrivateRoute>
              <ActivityTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/rplstatuses"
          element={
            <PrivateRoute>
              <RplStatusList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/rplstatuses/create"
          element={
            <PrivateRoute>
              <RplStatusCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/rplstatuses/:id"
          element={
            <PrivateRoute>
              <RplStatusEdit />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/rangestatuses"
          element={
            <PrivateRoute>
              <RangeStatusList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/rangestatuses/create"
          element={
            <PrivateRoute>
              <RangeStatusCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/rangestatuses/:id"
          element={
            <PrivateRoute>
              <RangeStatusEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/rangeshootingreasons"
          element={
            <PrivateRoute>
              <RangeShootingReasonList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/rangeshootingreasons/create"
          element={
            <PrivateRoute>
              <RangeShootingReasonCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/rangeshootingreasons/:id"
          element={
            <PrivateRoute>
              <RangeShootingReasonEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/memberregistrationsteps"
          element={
            <PrivateRoute>
              <MemberRegistrationStepList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberregistrationsteps/create"
          element={
            <PrivateRoute>
              <MemberRegistrationStepCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberregistrationsteps/:id"
          element={
            <PrivateRoute>
              <MemberRegistrationStepEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/evaluationstatuses"
          element={
            <PrivateRoute>
              <EvaluationStatusList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/evaluationstatuses/create"
          element={
            <PrivateRoute>
              <EvaluationStatusCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/evaluationstatuses/:id"
          element={
            <PrivateRoute>
              <EvaluationStatusEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/farmstatuses"
          element={
            <PrivateRoute>
              <FarmStatusList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/farmstatuses/create"
          element={
            <PrivateRoute>
              <FarmStatusCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/farmstatuses/:id"
          element={
            <PrivateRoute>
              <FarmStatusEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/invoicestatuses"
          element={
            <PrivateRoute>
              <InvoiceStatusList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/invoicestatuses/create"
          element={
            <PrivateRoute>
              <InvoiceStatusCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/invoicestatuses/:id"
          element={
            <PrivateRoute>
              <InvoiceStatusEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/scorestatuses"
          element={
            <PrivateRoute>
              <ScoreStatusList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/scorestatuses/create"
          element={
            <PrivateRoute>
              <ScoreStatusCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/scorestatuses/:id"
          element={
            <PrivateRoute>
              <ScoreStatusEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/competitionstatuses"
          element={
            <PrivateRoute>
              <CompetitionStatusList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/competitionstatuses/create"
          element={
            <PrivateRoute>
              <CompetitionStatusCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/competitionstatuses/:id"
          element={
            <PrivateRoute>
              <CompetitionStatusEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/endorsementstatuses"
          element={
            <PrivateRoute>
              <EndorsementStatusList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/endorsementstatuses/create"
          element={
            <PrivateRoute>
              <EndorsementStatusCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/endorsementstatuses/:id"
          element={
            <PrivateRoute>
              <EndorsementStatusEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/coursestatuses"
          element={
            <PrivateRoute>
              <CourseStatusList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/coursestatuses/create"
          element={
            <PrivateRoute>
              <CourseStatusCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/coursestatuses/:id"
          element={
            <PrivateRoute>
              <CourseStatusEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/memberstatuses"
          element={
            <PrivateRoute>
              <MemberStatusList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberstatuses/create"
          element={
            <PrivateRoute>
              <MemberStatusCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberstatuses/:id"
          element={
            <PrivateRoute>
              <MemberStatusEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/activitystatuses"
          element={
            <PrivateRoute>
              <ActivityStatusList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/activitystatuses/create"
          element={
            <PrivateRoute>
              <ActivityStatusCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/activitystatuses/:id"
          element={
            <PrivateRoute>
              <ActivityStatusEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/componentactions"
          element={
            <PrivateRoute>
              <ComponentActionList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/componentactions/create"
          element={
            <PrivateRoute>
              <ComponentActionCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/componentactions/:id"
          element={
            <PrivateRoute>
              <ComponentActionEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/replytemplates"
          element={
            <PrivateRoute>
              <ReplyTemplateList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/replytemplates/create"
          element={
            <PrivateRoute>
              <ReplyTemplateCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/replytemplates/:id"
          element={
            <PrivateRoute>
              <ReplyTemplateEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/rangetypes"
          element={
            <PrivateRoute>
              <RangeTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/rangetypes/create"
          element={
            <PrivateRoute>
              <RangeTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/rangetypes/:id"
          element={
            <PrivateRoute>
              <RangeTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/ranges"
          element={
            <PrivateRoute>
              <RangeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/ranges/create"
          element={
            <PrivateRoute>
              <RangeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/ranges/:id"
          element={
            <PrivateRoute>
              <RangeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/listingtypes"
          element={
            <PrivateRoute>
              <ListingTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/listingtypes/create"
          element={
            <PrivateRoute>
              <ListingTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/listingtypes/:id"
          element={
            <PrivateRoute>
              <ListingTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/listingstatuses"
          element={
            <PrivateRoute>
              <ListingStatusList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/listingstatuses/create"
          element={
            <PrivateRoute>
              <ListingStatusCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/listingstatuses/:id"
          element={
            <PrivateRoute>
              <ListingStatusEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/genders"
          element={
            <PrivateRoute>
              <GenderList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/genders/create"
          element={
            <PrivateRoute>
              <GenderCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/genders/:id"
          element={
            <PrivateRoute>
              <GenderEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/countries"
          element={
            <PrivateRoute>
              <CountryList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/countries/create"
          element={
            <PrivateRoute>
              <CountryCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/countries/:id"
          element={
            <PrivateRoute>
              <CountryEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/members"
          element={
            <PrivateRoute>
              <MemberList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/members/status/newregistration"
          element={
            <PrivateRoute>
              <MemberList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/members/status/active"
          element={
            <PrivateRoute>
              <MemberList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/members/status/none"
          element={
            <PrivateRoute>
              <MemberList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/members/status/archived"
          element={
            <PrivateRoute>
              <MemberList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/members/status/deleted"
          element={
            <PrivateRoute>
              <MemberList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/members/create"
          element={
            <PrivateRoute>
              <MemberCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/members/:id"
          element={
            <PrivateRoute>
              <MemberEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/listings"
          element={
            <PrivateRoute>
              <ListingList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/listings/create"
          element={
            <PrivateRoute>
              <ListingCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/listings/:id"
          element={
            <PrivateRoute>
              <ListingEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/questionsets"
          element={
            <PrivateRoute>
              <QuestionSetList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/questionsets/create"
          element={
            <PrivateRoute>
              <QuestionSetCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/questionsets/:id"
          element={
            <PrivateRoute>
              <QuestionSetEdit />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/questionsets/:questionSetId/questions/create"
          element={
            <PrivateRoute>
              <QuestionCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/questionsets/:questionSetId/questions/:questionId"
          element={
            <PrivateRoute>
              <QuestionEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/notetypes"
          element={
            <PrivateRoute>
              <NoteTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/notetypes/create"
          element={
            <PrivateRoute>
              <NoteTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/notetypes/:id"
          element={
            <PrivateRoute>
              <NoteTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/replytypes"
          element={
            <PrivateRoute>
              <ReplyTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/replytypes/create"
          element={
            <PrivateRoute>
              <ReplyTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/replytypes/:id"
          element={
            <PrivateRoute>
              <ReplyTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/invoicetypes"
          element={
            <PrivateRoute>
              <InvoiceTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/invoicetypes/create"
          element={
            <PrivateRoute>
              <InvoiceTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/invoicetypes/:id"
          element={
            <PrivateRoute>
              <InvoiceTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/paymenttypes"
          element={
            <PrivateRoute>
              <PaymentTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/paymenttypes/create"
          element={
            <PrivateRoute>
              <PaymentTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/paymenttypes/:id"
          element={
            <PrivateRoute>
              <PaymentTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/newsletters"
          element={
            <PrivateRoute>
              <NewsletterList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/newsletters/create"
          element={
            <PrivateRoute>
              <NewsletterCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/newsletters/:id"
          element={
            <PrivateRoute>
              <NewsletterEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/farmtypes"
          element={
            <PrivateRoute>
              <FarmTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/farmtypes/create"
          element={
            <PrivateRoute>
              <FarmTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/farmtypes/:id"
          element={
            <PrivateRoute>
              <FarmTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/certificatetypes"
          element={
            <PrivateRoute>
              <CertificateTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/certificatetypes/create"
          element={
            <PrivateRoute>
              <CertificateTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/certificatetypes/:id"
          element={
            <PrivateRoute>
              <CertificateTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/pages"
          element={
            <PrivateRoute>
              <PagesList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/pages/create"
          element={
            <PrivateRoute>
              <PagesCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/pages/:id"
          element={
            <PrivateRoute>
              <PagesEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/competencytypes"
          element={
            <PrivateRoute>
              <CompetencyTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/competencytypes/create"
          element={
            <PrivateRoute>
              <CompetencyTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/competencytypes/:id"
          element={
            <PrivateRoute>
              <CompetencyTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/scoretypes"
          element={
            <PrivateRoute>
              <ScoreTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/scoretypes/create"
          element={
            <PrivateRoute>
              <ScoreTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/scoretypes/:id"
          element={
            <PrivateRoute>
              <ScoreTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/recontypes"
          element={
            <PrivateRoute>
              <ReconTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/recontypes/create"
          element={
            <PrivateRoute>
              <ReconTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/recontypes/:id"
          element={
            <PrivateRoute>
              <ReconTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/coursetypes"
          element={
            <PrivateRoute>
              <CourseTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/coursetypes/create"
          element={
            <PrivateRoute>
              <CourseTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/coursetypes/:id"
          element={
            <PrivateRoute>
              <CourseTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/moduletypes"
          element={
            <PrivateRoute>
              <ModuleTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/moduletypes/create"
          element={
            <PrivateRoute>
              <ModuleTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/moduletypes/:id"
          element={
            <PrivateRoute>
              <ModuleTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/enquirytypes"
          element={
            <PrivateRoute>
              <EnquiryTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/enquirytypes/create"
          element={
            <PrivateRoute>
              <EnquiryTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/enquirytypes/:id"
          element={
            <PrivateRoute>
              <EnquiryTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/emailtypes"
          element={
            <PrivateRoute>
              <EmailTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/emailtypes/create"
          element={
            <PrivateRoute>
              <EmailTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/emailtypes/:id"
          element={
            <PrivateRoute>
              <EmailTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/components"
          element={
            <PrivateRoute>
              <ComponentList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/components/create"
          element={
            <PrivateRoute>
              <ComponentCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/components/:id"
          element={
            <PrivateRoute>
              <ComponentEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/exercisetemplates"
          element={
            <PrivateRoute>
              <ExerciseTemplateList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/exercisetemplates/create"
          element={
            <PrivateRoute>
              <ExerciseTemplateCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/exercisetemplates/:id"
          element={
            <PrivateRoute>
              <ExerciseTemplateEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/membercompetencies"
          element={
            <PrivateRoute>
              <MemberCompetencyList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/membercompetencies/create"
          element={
            <PrivateRoute>
              <MemberCompetencyCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/membercompetencies/:id"
          element={
            <PrivateRoute>
              <MemberCompetencyEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/memberfirearms"
          element={
            <PrivateRoute>
              <MemberFirearmList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberfirearms/create"
          element={
            <PrivateRoute>
              <MemberFirearmCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberfirearms/:id"
          element={
            <PrivateRoute>
              <MemberFirearmEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/memberendorsements"
          element={
            <PrivateRoute>
              <MemberEndorsementList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberendorsements/status/new"
          element={
            <PrivateRoute>
              <MemberEndorsementList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberendorsements/status/renewal"
          element={
            <PrivateRoute>
              <MemberEndorsementList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberendorsements/status/deleted"
          element={
            <PrivateRoute>
              <MemberEndorsementList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberendorsements/create"
          element={
            <PrivateRoute>
              <MemberEndorsementCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberendorsements/:id"
          element={
            <PrivateRoute>
              <MemberEndorsementEdit />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/membersetup"
          element={
            <PrivateRoute>
              <MemberSetup />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/competitions"
          element={
            <PrivateRoute>
              <CompetitionList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/competitions/create"
          element={
            <PrivateRoute>
              <CompetitionCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/competitions/:id"
          element={
            <PrivateRoute>
              <CompetitionEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/invoices"
          element={
            <PrivateRoute>
              <InvoiceList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/invoices/create"
          element={
            <PrivateRoute>
              <InvoiceCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/invoices/:id"
          element={
            <PrivateRoute>
              <InvoiceEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/invoicelineitems"
          element={
            <PrivateRoute>
              <InvoiceLineItemList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/invoicelineitems/create"
          element={
            <PrivateRoute>
              <InvoiceLineItemCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/invoicelineitems/:id"
          element={
            <PrivateRoute>
              <InvoiceLineItemEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/activities"
          element={
            <PrivateRoute>
              <ActivityList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/activities/create"
          element={
            <PrivateRoute>
              <ActivityCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/activities/:id"
          element={
            <PrivateRoute>
              <ActivityEdit />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/activities/status/deleted"
          element={
            <PrivateRoute>
              <ActivityList />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/activityspecies"
          element={
            <PrivateRoute>
              <ActivitySpeciesList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/activityspecies/create"
          element={
            <PrivateRoute>
              <ActivitySpeciesCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/activityspecies/:id"
          element={
            <PrivateRoute>
              <ActivitySpeciesEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/dedicatedstatus"
          element={
            <PrivateRoute>
              <DedicatedStatus />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/rpls"
          element={
            <PrivateRoute>
              <RplList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/rpls/create"
          element={
            <PrivateRoute>
              <RplCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/rpls/:id"
          element={
            <PrivateRoute>
              <RplEdit />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/rpls/status/sport"
          element={
            <PrivateRoute>
              <RplList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/rpls/status/hunt"
          element={
            <PrivateRoute>
              <RplList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/rpls/status/huntsport"
          element={
            <PrivateRoute>
              <RplList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/rpls/status/deleted"
          element={
            <PrivateRoute>
              <RplList />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/courses"
          element={
            <PrivateRoute>
              <CourseList />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/courses/create"
          element={
            <PrivateRoute>
              <CourseCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/courses/:id"
          element={
            <PrivateRoute>
              <CourseEdit />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/courses/status/print"
          element={
            <PrivateRoute>
              <CourseList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/courses/status/downloaded"
          element={
            <PrivateRoute>
              <CourseList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/courses/status/sport"
          element={
            <PrivateRoute>
              <CourseList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/courses/status/hunt"
          element={
            <PrivateRoute>
              <CourseList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/courses/status/huntsport"
          element={
            <PrivateRoute>
              <CourseList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/courses/status/deleted"
          element={
            <PrivateRoute>
              <CourseList />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/scores"
          element={
            <PrivateRoute>
              <ScoreList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/scores/create"
          element={
            <PrivateRoute>
              <ScoreCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/scores/:id"
          element={
            <PrivateRoute>
              <ScoreEdit />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/scores/evaluation"
          element={
            <PrivateRoute>
              <ScoreList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/scores/in-house"
          element={
            <PrivateRoute>
              <ScoreList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/scores/competition"
          element={
            <PrivateRoute>
              <ScoreList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/scores/deleted"
          element={
            <PrivateRoute>
              <ScoreList />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/courses/evaluation/:id"
          element={
            <PrivateRoute>
              <Evaluation />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/replies"
          element={
            <PrivateRoute>
              <EmailMessageThreadList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/replies/component/:componentName"
          element={
            <PrivateRoute>
              <EmailMessageThreadList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/replies/create"
          element={
            <PrivateRoute>
              <EmailMessageThreadCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/replies/:id"
          element={
            <PrivateRoute>
              <EmailMessageThreadEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/invoicebatches"
          element={
            <PrivateRoute>
              <InvoiceBatchList />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/invoicebatches/:batchId"
          element={
            <PrivateRoute>
              <InvoiceBatchDetail />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/memberfees"
          element={
            <PrivateRoute>
              <MemberFeeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberfees/create"
          element={
            <PrivateRoute>
              <MemberFeeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberfees/:id"
          element={
            <PrivateRoute>
              <MemberFeeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/notes"
          element={
            <PrivateRoute>
              <NoteList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/tasks"
          element={
            <PrivateRoute>
              <NoteList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/my-tasks"
          element={
            <PrivateRoute>
              <NoteList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/flags"
          element={
            <PrivateRoute>
              <NoteList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/notes/create"
          element={
            <PrivateRoute>
              <NoteCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/notes/:id"
          element={
            <PrivateRoute>
              <NoteEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/recontransactions"
          element={
            <PrivateRoute>
              <ReconTransactionList />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/recontransactions/confirmations"
          element={
            <PrivateRoute>
              <ReconTransactionList />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/recontransactions/unallocated"
          element={
            <PrivateRoute>
              <ReconTransactionList />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/enquiries"
          element={
            <PrivateRoute>
              <EnquiryList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/enquiries/create"
          element={
            <PrivateRoute>
              <EnquiryCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/enquiries/:id"
          element={
            <PrivateRoute>
              <EnquiryEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/competitionentries"
          element={
            <PrivateRoute>
              <CompetitionEntryList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/competitionentries/create"
          element={
            <PrivateRoute>
              <CompetitionEntryCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/competitionentries/:id"
          element={
            <PrivateRoute>
              <CompetitionEntryEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/memberadditionaldocuments"
          element={
            <PrivateRoute>
              <MemberAdditionalDocumentList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberadditionaldocuments/create"
          element={
            <PrivateRoute>
              <MemberAdditionalDocumentCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/memberadditionaldocuments/:id"
          element={
            <PrivateRoute>
              <MemberAdditionalDocumentEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/reports/finance/:type"
          element={
            <PrivateRoute>
              <FinanceExport />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/reports/courses/duplicate-examinee"
          element={
            <PrivateRoute>
              <CourseDuplicateExamineeExport />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/reports/members/life-members"
          element={
            <PrivateRoute>
              <LifeMembersExport />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/friendandpartners"
          element={
            <PrivateRoute>
              <FriendAndPartnerList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/friendandpartners/create"
          element={
            <PrivateRoute>
              <FriendAndPartnerCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/friendandpartners/:id"
          element={
            <PrivateRoute>
              <FriendAndPartnerEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/fellows"
          element={
            <PrivateRoute>
              <FellowList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/fellows/create"
          element={
            <PrivateRoute>
              <FellowCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/fellows/:id"
          element={
            <PrivateRoute>
              <FellowEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/forfeiturereasons"
          element={
            <PrivateRoute>
              <ForfeitureReasonList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/forfeiturereasons/create"
          element={
            <PrivateRoute>
              <ForfeitureReasonCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/forfeiturereasons/:id"
          element={
            <PrivateRoute>
              <ForfeitureReasonEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/competitionresults"
          element={
            <PrivateRoute>
              <CompetitionResultList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/competitionresults/my-results"
          element={
            <PrivateRoute>
              <CompetitionResultMyResults />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/competitionresults/create"
          element={
            <PrivateRoute>
              <CompetitionResultCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/competitionresults/:id"
          element={
            <PrivateRoute>
              <CompetitionResultEdit />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/competitionresults/:id/exercises"
          element={
            <PrivateRoute>
              <CompetitionResultExercises />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/competitionresults/:id/exercises/:competitionExerciseId"
          element={
            <PrivateRoute>
              <CompetitionResultEntries />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/competitionresultentries"
          element={
            <PrivateRoute>
              <CompetitionResultEntryList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/competitionresultentries/create"
          element={
            <PrivateRoute>
              <CompetitionResultEntryCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/competitionresultentries/:id"
          element={
            <PrivateRoute>
              <CompetitionResultEntryEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/contentcategories"
          element={
            <PrivateRoute>
              <ContentCategoryList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/contentcategories/create"
          element={
            <PrivateRoute>
              <ContentCategoryCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/contentcategories/:id"
          element={
            <PrivateRoute>
              <ContentCategoryEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/articles"
          element={
            <PrivateRoute>
              <ArticleList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/articles/create"
          element={
            <PrivateRoute>
              <ArticleCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/articles/:id"
          element={
            <PrivateRoute>
              <ArticleEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/newsitems"
          element={
            <PrivateRoute>
              <NewsItemList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/newsitems/create"
          element={
            <PrivateRoute>
              <NewsItemCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/newsitems/:id"
          element={
            <PrivateRoute>
              <NewsItemEdit />
            </PrivateRoute>
          }
        />




      </Routes >
    </Router >
  </ViewProvider >
);

export default AppRouter;
